import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { FieldLabel } from '../../shared/ui/field-label';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Textarea } from '../../shared/ui/textarea';
import { ErrorState } from '../../shared/ui/states';
import {
  bytesWords,
  holdersWords,
  pinsForVersion,
  pinTitle,
  type IPin,
} from '../../entities/transform';
import {
  createLibraryVersion,
  libraryKeys,
  type TLibraryItem,
  type TLibraryMessage,
} from '../../entities/library';

export type TLibraryBody = { code: string } | { messages: TLibraryMessage[] };

interface IProps {
  item: TLibraryItem;
  body: TLibraryBody;
  basedOn: number;
  pins: IPin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublished: (version: number) => void;
}

const bodyWords = (body: TLibraryBody): string =>
  'code' in body
    ? `${bytesWords(new Blob([body.code]).size)} of code`
    : `${body.messages.length} message${body.messages.length === 1 ? '' : 's'}`;

export const LibraryPublish = ({
  item,
  body,
  basedOn,
  pins,
  open,
  onOpenChange,
  onPublished,
}: IProps) => {
  const [comment, setComment] = useState('');
  // `latestVersion` moves the moment the save lands, so the title is held on the number the version was given.
  const [born, setBorn] = useState<number | null>(null);
  const client = useQueryClient();
  const next = item.latestVersion + 1;
  const held = pinsForVersion(pins, basedOn);

  useEffect(() => {
    if (!open) return;
    setBorn(null);
    setComment('');
  }, [open]);

  const publish = useMutation({
    mutationFn: () =>
      createLibraryVersion(item.id, { ...body, ...(comment.trim() ? { comment } : {}) }),
    // The page moves to the new version at once: behind this dialog it must not still read
    // "v1 · edited, publishing writes v3".
    onSuccess: (created) => {
      setBorn(created.version);
      onPublished(created.version);
      void client.invalidateQueries({ queryKey: libraryKeys.all });
    },
  });

  const close = () => onOpenChange(false);

  if (born !== null) {
    return (
      <Modal
        open={open}
        onOpenChange={close}
        title={`v${born} published`}
        width="md"
        footer={
          <Button size="sm" onClick={close}>
            Close
          </Button>
        }
      >
        <Note>
          v{born} exists now, and it is running nowhere. Nothing was repointed at it — move a step
          to it from the step&rsquo;s own settings.
        </Note>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={`Publish v${next}`}
      width="md"
      description={`${bodyWords(body)}, edited against v${basedOn}.`}
      cancelLabel="Not yet"
      submit={{
        label: `Publish v${next}`,
        onSubmit: () => publish.mutate(),
        waiting: publish.isPending,
        waitingLabel: 'Publishing',
      }}
    >
      <div>
        <FieldLabel htmlFor="publish-comment">Why this version exists</FieldLabel>
        <Textarea
          id="publish-comment"
          rows={3}
          maxLength={300}
          placeholder={'code' in body ? 'currency fallback added' : 'asks for one word'}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />

        <Note className="mt-4">
          Publishing does nothing on its own. A version only runs once a step or rule pins it, so v
          {next} will be born changing nothing.{' '}
          {held.length > 0
            ? `${holdersWords(held)} pinned to v${basedOn} (${held
                .map(pinTitle)
                .join(
                  ', ',
                )}) ${held.length === 1 ? 'keeps' : 'keep'} running v${basedOn} until their steps say otherwise.`
            : pins.length > 0
              ? `Nothing is pinned to v${basedOn}; the ${holdersWords(pins)} that use it stay on the versions they name.`
              : 'No step pins it yet; publishing it changes nothing anywhere.'}
        </Note>

        {publish.isError && <ErrorState error={publish.error} />}
      </div>
    </Modal>
  );
};
